import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStatementOperationsAndInterests } from '@/services/admin-services/query/useStatementOperationsAndInterests';
import { useAuthContext } from '@/context/AuthContext';
import useStatementData from '@/hooks/global-hooks/useStatementData';
import { useToast } from '@/components/organisms/feedback/use-toast';
import { useConsultantInvoice } from '@/services/global-services/query/useConsultantInvoice';
import { useCompanyBenefitInvoice } from '@/services/global-services/query/useCompanyBenefitInvoice';
import { useExpenseReport } from '@/services/global-services/query/useExpenseReport';
import { usePayslip } from '@/services/global-services/query/usePayslip';
import { useAddStatementOperation } from '@/services/admin-services/mutations/useAddStatementOperation';
import { useUpdateStatementOperation } from '@/services/admin-services/mutations/useUpdateStatementOperation';
import { useUpdateStatementOperationAlwaysEditable } from '@/services/admin-services/mutations/useUpdateStatementOperationAlwaysEditable';
import { useDeleteStatementOperation } from '@/services/admin-services/mutations/useDeleteStatementOperation';
import { useAppData } from '@/hooks/global-hooks/AppDataContext';

export const useOveflowAccountTab = ({ isAdmin = false }) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const month = today.getMonth() + 1;

    const [detailledEvent, setDetailledEvent] = useState(false);
    const [selectedYear, setSelectedYear] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingOperation, setEditingOperation] = useState(null);
    const [editMode, setEditMode] = useState('none');
    const [submissionError, setSubmissionError] = useState(null);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const [alwaysEditableForm, setAlwaysEditableForm] = useState({ impactsOverflow: true, comment: '' });
    const [initialAlwaysEditableForm, setInitialAlwaysEditableForm] = useState({ impactsOverflow: true, comment: '' });
    const [editOperationForm, setEditOperationForm] = useState({
        valueDate: new Date().toISOString().slice(0, 10),
        operationType: 'OTHER',
        amount: '',
        currency: 'EUR',
        comment: '',
        impactsOverflow: true,
    });
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [operationToDelete, setOperationToDelete] = useState(null);
    const [createOperationForm, setCreateOperationForm] = useState({
        valueDate: new Date().toISOString().slice(0, 10),
        operationType: 'OTHER',
        amount: '',
        currency: 'EUR',
        comment: '',
        impactsOverflow: true,
    });

    const submitRef = React.useRef(null);

    const { id } = useParams();
    const { hasPrivileges } = useAuthContext();
    const { toast } = useToast();
    const [t] = useTranslation('global');
    const { currencies, loadingCurrencies, errorCurrencies } = useAppData();
    const canManageStatement = !!(isAdmin && hasPrivileges?.('ADMIN_STATEMENT'));

    const consultantInvoiceHook = useConsultantInvoice();
    const companyBenefitInvoiceHook = useCompanyBenefitInvoice();
    const expenseReportHook = useExpenseReport();
    const payslipHook = usePayslip();

    const { addStatementOperation, loading: addLoading } = useAddStatementOperation();
    const { updateStatementOperation, loading: updateLoading } = useUpdateStatementOperation();
    const { updateStatementOperationAlwaysEditable, loading: updateAlwaysLoading } = useUpdateStatementOperationAlwaysEditable();
    const { deleteStatementOperation, loading: deleteLoading } = useDeleteStatementOperation();

    const {
        allYears,
        yearStatement,
        yearStatementLoading,
        yearStatementError
    } = useStatementData(isAdmin, selectedYear || currentYear, id);

    useEffect(() => {
        setDetailledEvent(false);
    }, [id]);

    const year = selectedYear || currentYear;
    const { data, loading, error, refetch } = useStatementOperationsAndInterests(year, undefined, isAdmin, id);

    const getSelectedEntityHookData = () => {
        switch (editingOperation?.operationType) {
            case 'CONSULTANT_INVOICE':
                return { data: consultantInvoiceHook.data, loading: consultantInvoiceHook.loading, error: consultantInvoiceHook.error };
            case 'PAYSLIP':
                return { data: payslipHook.data, loading: payslipHook.loading, error: payslipHook.error };
            case 'EXPENSE_REPORT':
                return { data: expenseReportHook.data, loading: expenseReportHook.loading, error: expenseReportHook.error };
            case 'COMPANY_BENEFIT_INVOICE':
                return { data: companyBenefitInvoiceHook.data, loading: companyBenefitInvoiceHook.loading, error: companyBenefitInvoiceHook.error };
            default:
                return { data: null, loading: false, error: null };
        }
    };

    const getOperationTypeModalType = (operationType) => {
        switch (operationType) {
            case 'PAYSLIP':
                return 'payslip';
            case 'EXPENSE_REPORT':
                return 'expense';
            case 'COMPANY_BENEFIT_INVOICE':
                return 'companyBenefit';
            default:
                return 'timesheet';
        }
    };

    const getModalTitle = (operationType) => {
        switch (operationType) {
            case 'PAY_INTERESTS':
                return 'consultants.statement.statementOperations.statementOperationType_PAY_INTERESTS';
            case 'INITIALIZATION':
                return 'consultants.statement.statementOperations.statementOperationType_INITIALIZATION';
            case 'OTHER':
                return 'consultants.statement.statementOperations.statementOperationType_OTHER';
            case 'PAYSLIP':
                return 'consultants.statement.statementOperations.statementOperationType_PAYSLIP';
            case 'EXPENSE_REPORT':
                return 'consultants.statement.statementOperations.statementOperationType_EXPENSE_REPORT';
            case 'CONSULTANT_INVOICE':
                return 'consultants.statement.statementOperations.statementOperationType_CONSULTANT_INVOICE';
            case 'COMPANY_BENEFIT_INVOICE':
                return 'consultants.statement.statementOperations.statementOperationType_COMPANY_BENEFIT_INVOICE';
            default:
                return 'global.error.requestFailed';
        }
    };

    const getSignedAmount = (rawValue, originalAmount) => {
        const parsed = Number(rawValue);
        if (Number.isNaN(parsed)) return Number(originalAmount ?? 0);
        const originalSign = Number(originalAmount ?? 0) < 0 ? -1 : 1;
        return Math.abs(parsed) * originalSign;
    };

    const buildUpdatedStatementOperationFromForm = (operation, formData) => {
        const baseAmount = Number(operation?.amountEur ?? operation?.amount ?? 0);
        const baseDate = operation?.valueDate;

        const common = {
            valueDate: formData?.paymentDate || formData?.invoiceDate || baseDate,
            operationType: operation?.operationType,
            amount: baseAmount,
            currency: formData?.currency || operation?.currency || 'EUR',
            comment: formData?.comment ?? operation?.comment ?? null,
            linkedEntityUuid: operation?.linkedEntityUuid || null,
            linkedEntityPeriod: formData?.period || operation?.linkedEntityPeriod || null,
        };

        switch (operation?.operationType) {
            case 'CONSULTANT_INVOICE':
                return {
                    ...common,
                    valueDate: formData?.paymentDate || formData?.invoiceDate || baseDate,
                    amount: getSignedAmount(formData?.amountConsultant, baseAmount),
                };
            case 'EXPENSE_REPORT':
                return {
                    ...common,
                    valueDate: formData?.paymentDate || formData?.invoiceDate || baseDate,
                    amount: getSignedAmount(formData?.totalAmount, baseAmount),
                };
            case 'COMPANY_BENEFIT_INVOICE':
                return {
                    ...common,
                    valueDate: formData?.invoiceDate || baseDate,
                    amount: getSignedAmount(formData?.totalAmount, baseAmount),
                };
            case 'PAYSLIP':
                return {
                    ...common,
                    valueDate: formData?.paymentDate || baseDate,
                };
            default:
                return common;
        }
    };

    const handleOpenEditOperation = async (operation) => {
        if (!canManageStatement) return;
        setSubmissionError(null);
        setSubmissionSuccess(false);
        setEditingOperation(operation);

        if (operation?.editable === false) {
            const initialForm = {
                impactsOverflow: operation?.impactsOverflow !== false,
                comment: operation?.comment || '',
            };
            setAlwaysEditableForm(initialForm);
            setInitialAlwaysEditableForm(initialForm);
            setEditMode('alwaysEditable');
            setIsEditModalOpen(true);
            return;
        }

        const supportedTypes = ['CONSULTANT_INVOICE', 'PAYSLIP', 'EXPENSE_REPORT', 'COMPANY_BENEFIT_INVOICE'];
        if (!supportedTypes.includes(operation?.operationType)) {
            setEditOperationForm({
                valueDate: (operation?.valueDate || '').slice(0, 10),
                operationType: operation?.operationType || 'OTHER',
                amount: String(operation?.amountEur ?? operation?.amount ?? ''),
                currency: operation?.currency || 'EUR',
                comment: operation?.comment || '',
                impactsOverflow: operation?.impactsOverflow !== false,
            });
            setEditMode('manualForm');
            setIsEditModalOpen(true);
            return;
        }

        setEditMode('typedForm');
        if (operation?.linkedEntityUuid) {
            if (operation.operationType === 'CONSULTANT_INVOICE') await consultantInvoiceHook.getConsultantInvoice(operation.linkedEntityUuid);
            if (operation.operationType === 'PAYSLIP') await payslipHook.getPayslip(operation.linkedEntityUuid);
            if (operation.operationType === 'EXPENSE_REPORT') await expenseReportHook.getExpenseReport(operation.linkedEntityUuid);
            if (operation.operationType === 'COMPANY_BENEFIT_INVOICE') await companyBenefitInvoiceHook.getCompanyBenefitInvoice(operation.linkedEntityUuid);
        }
        setIsEditModalOpen(true);
    };

    const handleDeleteOperation = async (operation) => {
        if (!canManageStatement) return;

        if (operation?.editable === false) {
            toast({
                variant: 'warning',
                title: 'error.requestFailed',
                description: 'Cette opération n’est pas supprimable (editable=false).',
                duration: 4000,
            });
            return;
        }
        if (operation?.linkedEntityUuid) {
            toast({
                variant: 'warning',
                title: 'error.requestFailed',
                description: 'Cette opération est liée à une entité et ne peut pas être supprimée.',
                duration: 4000,
            });
            return;
        }

        try {
            await deleteStatementOperation({
                variables: {
                    deletedStatementOperationUuid: operation.uuid,
                    valueDate: operation.valueDate,
                },
            });
            toast({ variant: 'success', title: 'success.backendOperation.delete', duration: 3000 });
            refetch?.();
        } catch (err) {
            toast({ variant: 'error', title: 'error.requestFailed', description: err?.message, duration: 5000 });
        }
    };

    const handleRequestDeleteOperation = (operation) => {
        if (!canManageStatement) return;
        setOperationToDelete(operation);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDeleteOperation = async () => {
        if (!operationToDelete) return;
        await handleDeleteOperation(operationToDelete);
        setDeleteConfirmOpen(false);
        setOperationToDelete(null);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteConfirmOpen(false);
        setOperationToDelete(null);
    };

    const hasUnsavedChanges = () => {
        if (editMode !== 'alwaysEditable') return false;
        return (
            alwaysEditableForm.impactsOverflow !== initialAlwaysEditableForm.impactsOverflow ||
            alwaysEditableForm.comment !== initialAlwaysEditableForm.comment
        );
    };

    const handleEditModalClose = () => {
        if (hasUnsavedChanges()) {
            toast({
                variant: 'warning',
                title: 'Vous n\'avez pas enregistré',
                description: 'L\'opération n\'a pas été modifiée',
                duration: 3000,
            });
        }
        setIsEditModalOpen(false);
        setEditingOperation(null);
        setSubmissionError(null);
        setSubmissionSuccess(false);
    };

    const handleSubmitAlwaysEditable = async () => {
        if (!editingOperation) return;
        const validatedComment = (alwaysEditableForm.comment || '').trim();
        try {
            await updateStatementOperationAlwaysEditable({
                variables: {
                    statementOperationUuid: editingOperation.uuid,
                    oldValueDate: editingOperation.valueDate,
                    impactsOverflow: !!alwaysEditableForm.impactsOverflow,
                    comment: validatedComment || null,
                },
            });
            setAlwaysEditableForm({ impactsOverflow: true, comment: '' });
            setIsEditModalOpen(false);
            setEditingOperation(null);
            setInitialAlwaysEditableForm({ impactsOverflow: true, comment: '' });
            toast({ variant: 'success', title: 'success.backendOperation.update', duration: 3000 });
            refetch?.();
        } catch (err) {
            setSubmissionError(err?.message || err);
        }
    };

    const handleSubmitTypedForm = async (formData) => {
        if (!editingOperation) return {};
        const validatedComment = (formData?.comment ?? editingOperation?.comment ?? '').trim();
        try {
            const updatedStatementOperation = buildUpdatedStatementOperationFromForm(editingOperation, {
                ...formData,
                comment: validatedComment || null,
            });
            await updateStatementOperation({
                variables: {
                    statementOperationUuid: editingOperation.uuid,
                    oldValueDate: editingOperation.valueDate,
                    updatedStatementOperation,
                },
            });
            setSubmissionSuccess(true);
            setIsEditModalOpen(false);
            setEditingOperation(null);
            toast({ variant: 'success', title: 'success.backendOperation.update', duration: 3000 });
            refetch?.();
            return { submissionSuccess: true };
        } catch (err) {
            setSubmissionError(err?.message || err);
            return { submissionError: err };
        }
    };

    const handleSubmitManualEditOperation = async () => {
        if (!editingOperation) return {};

        try {
            const updatedStatementOperation = {
                valueDate: editOperationForm.valueDate,
                operationType: editOperationForm.operationType,
                amount: Number(editOperationForm.amount || 0),
                currency: editOperationForm.currency || 'EUR',
                comment: (editOperationForm.comment || '').trim() || null,
                impactsOverflow: !!editOperationForm.impactsOverflow,
                linkedEntityUuid: editingOperation?.linkedEntityUuid || null,
                linkedEntityPeriod: editingOperation?.linkedEntityPeriod || null,
            };

            await updateStatementOperation({
                variables: {
                    statementOperationUuid: editingOperation.uuid,
                    oldValueDate: editingOperation.valueDate,
                    updatedStatementOperation,
                },
            });

            setSubmissionSuccess(true);
            setIsEditModalOpen(false);
            setEditingOperation(null);
            toast({ variant: 'success', title: 'success.backendOperation.update', duration: 3000 });
            refetch?.();
            return { submissionSuccess: true };
        } catch (err) {
            setSubmissionError(err?.message || err);
            return { submissionError: err };
        }
    };

    const handleCreateManualOperation = async () => {
        if (!id) return;
        try {
            await addStatementOperation({
                variables: {
                    consultantUuid: id,
                    newStatementOperation: {
                        valueDate: createOperationForm.valueDate,
                        operationType: createOperationForm.operationType,
                        amount: Number(createOperationForm.amount || 0),
                        currency: createOperationForm.currency,
                        comment: createOperationForm.comment || null,
                        impactsOverflow: createOperationForm.impactsOverflow,
                    },
                },
            });
            setIsCreateModalOpen(false);
            setCreateOperationForm({
                valueDate: new Date().toISOString().slice(0, 10),
                operationType: 'OTHER',
                amount: '',
                currency: 'EUR',
                comment: '',
                impactsOverflow: true,
            });
            toast({ variant: 'success', title: 'success.backendOperation.add', duration: 3000 });
            refetch?.();
        } catch (err) {
            toast({ variant: 'error', title: 'error.requestFailed', description: err?.message, duration: 5000 });
        }
    };

    const isAlwaysEditableSubmitDisabled = false;
    const isManualEditSubmitDisabled =
        editMode === 'manualForm' && (
            !editOperationForm.valueDate ||
            !editOperationForm.operationType ||
            editOperationForm.amount === '' ||
            !(editOperationForm.currency || '').trim()
        );
    const isCreateOperationSubmitDisabled =
        !createOperationForm.valueDate ||
        !createOperationForm.operationType ||
        createOperationForm.amount === '' ||
        !(createOperationForm.currency || '').trim();

    const displayedBalance = useMemo(() => {
        const days = data?.days || [];
        const isCurrentYear = year === currentYear;

        if (isCurrentYear) {
            const dayNum = today.getDate();
            const monthNum = today.getMonth() + 1;
            const todayString = `${year}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            const todayEntry = days.find?.((d) => d.day === todayString);

            if (todayEntry) {
                const ops = todayEntry.statementOperations || [];
                if (ops.length > 0) {
                    const lastOp = ops[ops.length - 1];
                    if (lastOp?.afterBalance !== undefined && lastOp?.afterBalance !== null) {
                        return { balance: Number(lastOp.afterBalance), date: today };
                    }
                }
                const ints = todayEntry.statementInterests || [];
                if (ints.length > 0) {
                    const lastInt = ints[ints.length - 1];
                    if (lastInt?.interestsBalanceEur !== undefined && lastInt?.interestsBalanceEur !== null) {
                        return { balance: Number(lastInt.interestsBalanceEur), date: today };
                    }
                }
            }
        }

        let lastBalance = null;
        for (let i = days.length - 1; i >= 0; i--) {
            const d = days[i];
            const ops = d.statementOperations || [];
            if (ops.length > 0) {
                const lastOp = ops[ops.length - 1];
                if (lastOp?.afterBalance !== undefined && lastOp?.afterBalance !== null) {
                    lastBalance = Number(lastOp.afterBalance);
                    break;
                }
            }
            const ints = d.statementInterests || [];
            if (ints.length > 0) {
                const lastInt = ints[ints.length - 1];
                if (lastInt?.interestsBalanceEur !== undefined && lastInt?.interestsBalanceEur !== null) {
                    lastBalance = Number(lastInt.interestsBalanceEur);
                    break;
                }
            }
        }

        if (lastBalance !== null) {
            const displayDate = isCurrentYear ? today : new Date(year, 11, 31);
            return { balance: lastBalance, date: displayDate };
        }

        return null;
    }, [data, year, currentYear, today]);

    const groups = useMemo(() => {
        const days = data?.days || [];
        const groupsMaker = [];
        for (const d of days) {
            const groupDate = Array.isArray(d.statementOperations) && d.statementOperations[0]?.valueDate
                ? new Date(d.statementOperations[0].valueDate)
                : Array.isArray(d.statementInterests) && d.statementInterests[0]?.interestDate
                    ? new Date(d.statementInterests[0].interestDate)
                    : (!Array.isArray(d.statementInterests) && d.statementInterests?.interestDate)
                        ? new Date(d.statementInterests.interestDate)
                        : new Date(year, month - 1, Number(d.day));

            const label = groupDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

            const ops = Array.isArray(d?.statementOperations) ? d.statementOperations : [];
            const evenements = [
                ...ops,
            ];

            const total = evenements.reduce((acc, ev) => {
                return acc + Number(ev.amountEur ?? ev.amount ?? 0);
            }, 0);

            groupsMaker.push({
                key: `${year}-${String(month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`,
                date: groupDate,
                label,
                evenements,
                total,
            });
        }
        return groupsMaker.sort((a, b) => b.date - a.date);
    }, [data, month, year]);

    const filteredGroups = useMemo(() => {
        return groups.filter((group) => group.evenements.some((ev) => Number(ev.amountEur ?? ev.amount ?? 0) !== 0));
    }, [groups]);

    return {
        t,
        year,
        loading,
        error,
        addLoading,
        updateLoading,
        updateAlwaysLoading,
        deleteLoading,
        allYears,
        yearStatement,
        yearStatementLoading,
        yearStatementError,
        filteredGroups,
        displayedBalance,
        canManageStatement,
        detailledEvent,
        setDetailledEvent,
        selectedYear,
        setSelectedYear,
        submitRef,
        isEditModalOpen,
        editingOperation,
        editMode,
        submissionError,
        submissionSuccess,
        setSubmissionSuccess,
        setSubmissionError,
        alwaysEditableForm,
        setAlwaysEditableForm,
        editOperationForm,
        setEditOperationForm,
        isCreateModalOpen,
        setIsCreateModalOpen,
        deleteConfirmOpen,
        operationToDelete,
        createOperationForm,
        setCreateOperationForm,
        isAlwaysEditableSubmitDisabled,
        isManualEditSubmitDisabled,
        isCreateOperationSubmitDisabled,
        currencies,
        loadingCurrencies,
        errorCurrencies,
        getSelectedEntityHookData,
        getOperationTypeModalType,
        getModalTitle,
        handleOpenEditOperation,
        handleRequestDeleteOperation,
        handleConfirmDeleteOperation,
        handleCloseDeleteDialog,
        handleEditModalClose,
        handleSubmitAlwaysEditable,
        handleSubmitManualEditOperation,
        handleSubmitTypedForm,
        handleCreateManualOperation,
    };
};
